import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  ServerIcon,
  CreditCardIcon,
  UserIcon,
  LogOutIcon,
  MenuIcon,
  XIcon,
  UsersIcon,
  SettingsIcon,
  FileTextIcon,
  ShieldIcon,
  AlertTriangleIcon,
  PhoneIcon
} from 'lucide-react';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Serviços', href: '/services', icon: ServerIcon },
    { name: 'Relatórios de Chamadas', href: '/call-reports', icon: PhoneIcon },
    { name: 'Faturas', href: '/invoices', icon: CreditCardIcon },
    { name: 'Perfil', href: '/profile', icon: UserIcon },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: SettingsIcon },
    { name: 'Usuários', href: '/admin/users', icon: UsersIcon },
    { name: 'Serviços', href: '/admin/services', icon: ServerIcon },
    { name: 'Servidores', href: '/admin/servers', icon: ServerIcon },
    { name: 'Planos', href: '/admin/plans', icon: CreditCardIcon },
    { name: 'Rate Limiting', href: '/admin/rate-limit', icon: ShieldIcon },
    { name: 'Dashboard de Segurança', href: '/admin/security', icon: AlertTriangleIcon },
    { name: 'Logs de Auditoria', href: '/admin/audit-logs', icon: FileTextIcon },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex">
      {/* Sidebar */}
      <div className={`bg-white/95 backdrop-blur-xl border-r border-white/20 transition-all duration-300 ease-out ${sidebarOpen ? 'shadow-2xl' : 'shadow-lg'} ${sidebarOpen ? 'w-64' : 'w-16'} flex flex-col fixed h-full z-20 will-change-transform`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100/50">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <>
                <div className="flex items-center space-x-2">
                  <img
                    src="/logo-wabbycall.svg"
                    alt="WabbyCall"
                    className="h-8 w-auto rounded-lg"
                  />
                  <div>
                    <span className="text-lg font-bold" style={{fontFamily: 'Orbitron, sans-serif', color: '#31b4e7'}}>WabbyCall</span>
                    <div className="w-6 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mt-0.5"></div>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 hover:bg-gray-100/80 rounded-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                >
                  <XIcon className="w-4 h-4 text-gray-500" />
                </button>
              </>
            ) : (
              <button
                onClick={() => setSidebarOpen(true)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gradient-to-br hover:from-cyan-50 hover:to-blue-50 rounded-lg transition-all duration-300 hover:scale-105 shadow-md cursor-pointer"
              >
                <MenuIcon className="w-4 h-4 text-cyan-600" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 py-4">
          <nav className="space-y-2 px-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ease-out overflow-hidden cursor-pointer will-change-transform ${
                  isActive(item.href)
                    ? `${sidebarOpen ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md' : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md w-12 h-12 rounded-xl'}`
                    : 'text-gray-600 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-700 hover:shadow-sm'
                } ${sidebarOpen ? '' : 'justify-center w-12'}`}
              >
                <div className={`w-8 h-8 rounded-lg transition-all duration-150 ease-out flex items-center justify-center will-change-transform ${
                  isActive(item.href)
                    ? 'bg-white/20'
                    : 'bg-gray-100/80 group-hover:bg-cyan-100/80'
                }`}>
                  <item.icon className={`${sidebarOpen ? 'h-4 w-4' : 'h-4 w-4'} transition-colors duration-150 ease-out`} />
                </div>
                {sidebarOpen && (
                  <span className="ml-3 font-medium text-sm">{item.name}</span>
                )}
                {sidebarOpen && isActive(item.href) && (
                  <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                )}
              </Link>
            ))}
            
            {user?.is_admin && (
              <>
                {sidebarOpen && (
                  <div className="border-t border-gradient-to-r from-gray-200/50 to-gray-300/50 mt-4 pt-4 px-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <SettingsIcon className="h-4 w-4 text-white" />
                      </div>
                      {sidebarOpen && (
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Administração
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {adminNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ease-out overflow-hidden cursor-pointer will-change-transform ${
                        isActive(item.href)
                          ? `${sidebarOpen ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md' : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md w-12 h-12 rounded-xl'}`
                          : 'text-gray-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-purple-700 hover:shadow-sm'
                      } ${sidebarOpen ? '' : 'justify-center w-12'}`}
                    >
                      <div className={`w-8 h-8 rounded-lg transition-all duration-150 ease-out flex items-center justify-center will-change-transform ${
                        isActive(item.href)
                          ? 'bg-white/20'
                          : 'bg-gray-100/80 group-hover:bg-purple-100/80'
                      }`}>
                        <item.icon className={`${sidebarOpen ? 'h-4 w-4' : 'h-4 w-4'} transition-colors duration-150 ease-out`} />
                      </div>
                      {sidebarOpen && (
                        <span className="ml-3 font-medium text-sm">{item.name}</span>
                      )}
                      {sidebarOpen && isActive(item.href) && (
                        <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                      )}
                    </Link>
                  ))}
                </div>
              </>
            )}

          </nav>
        </div>

        {/* Bottom section */}
        <div className="p-3 border-t border-gray-100/50">
          <div className="space-y-2">
            <button
              onClick={handleLogout}
              className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 hover:text-red-600 rounded-xl transition-all duration-200 ease-out hover:shadow-sm overflow-hidden cursor-pointer will-change-transform ${sidebarOpen ? '' : 'justify-center w-12'}`}
            >
              <div className={`w-8 h-8 rounded-lg transition-all duration-150 ease-out flex items-center justify-center will-change-transform ${
                'bg-gray-100/80 group-hover:bg-red-100/80'
              }`}>
                <LogOutIcon className={`${sidebarOpen ? 'h-4 w-4' : 'h-4 w-4'} transition-colors duration-150 ease-out`} />
              </div>
              {sidebarOpen && (
                <span className="ml-3 font-medium text-sm">Sair</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity ease-linear duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setSidebarOpen(false)} />

        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white/95 backdrop-blur-xl shadow-2xl transform transition ease-in-out duration-500 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-12 w-12 rounded-2xl bg-white/20 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white shadow-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <XIcon className="h-6 w-6 text-gray-700" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-6 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4 mb-6">
              <img
                src="/logo-wabbycall.svg"
                alt="WabbyCall"
                className="h-8 w-auto rounded-lg mr-3"
              />
              <div>
                <h1 className="text-lg font-bold" style={{fontFamily: 'Orbitron, sans-serif', color: '#31b4e7'}}>WabbyCall</h1>
                <div className="w-6 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mt-0.5"></div>
              </div>
            </div>
            <nav className="px-3 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-700'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className={`p-1.5 rounded-lg transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-white/20'
                      : 'bg-gray-100/80 group-hover:bg-cyan-100/80'
                  }`}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <span className="ml-3 font-medium">{item.name}</span>
                  {isActive(item.href) && (
                    <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                  )}
                </Link>
              ))}

              {user?.is_admin && (
                <>
                  <div className="border-t border-gradient-to-r from-gray-200/50 to-gray-300/50 mt-4 pt-4 px-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-md flex items-center justify-center">
                        <SettingsIcon className="h-2.5 w-2.5 text-white" />
                      </div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Administração
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {adminNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md'
                            : 'text-gray-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-purple-700'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <div className={`p-1.5 rounded-lg transition-all duration-300 ${
                          isActive(item.href)
                            ? 'bg-white/20'
                            : 'bg-gray-100/80 group-hover:bg-purple-100/80'
                        }`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        <span className="ml-3 font-medium">{item.name}</span>
                        {isActive(item.href) && (
                          <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
                        )}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ease-out ${sidebarOpen ? 'ml-64' : 'ml-16'} min-h-screen will-change-transform`}>
        {/* Mobile menu button */}
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white border-b border-gray-200">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Top bar */}
        <div className="bg-white/95 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-20">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <div className="flex items-center">
                <div className="flex items-center space-x-3">
                  <img
                    src="/logo-wabbycall.svg"
                    alt="WabbyCall"
                    className="h-8 w-auto rounded-lg"
                  />
                  <div>
                    <span className="text-lg font-bold text-gray-900">
                      {location.pathname === '/dashboard' && 'Dashboard'}
                      {location.pathname === '/services' && 'Serviços'}
                      {location.pathname === '/call-reports' && 'Relatórios de Chamadas'}
                      {location.pathname === '/invoices' && 'Faturas'}
                      {location.pathname.startsWith('/invoice/') && 'Fatura'}
                      {location.pathname === '/profile' && 'Perfil'}
                      {location.pathname.startsWith('/admin') && 'Administração'}
                    </span>
                    <div className="w-8 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mt-0.5"></div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/profile')}
                  className="group flex items-center space-x-3 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 px-3 py-2 rounded-lg transition-all duration-300 hover:shadow-md hover:transform hover:scale-105"
                >
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-cyan-800">{user?.name}</p>
                    <p className="text-xs text-gray-500 group-hover:text-cyan-600">{user?.email}</p>
                  </div>
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300">
                    <UserIcon className="w-5 h-5 text-gray-600 group-hover:text-cyan-700" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              {children ? children : <Outlet />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;